/**
 * Badges V2 Actions (Novo - Hierárquico)
 *
 * Este arquivo gerencia as actions para o sistema de badges V2 (hierárquico/tree).
 * Usado pelo componente BadgesTree.jsx (interface nova com árvore).
 *
 * Rotas: /api/v2/badges/tree, /api/v2/admin/badges/*
 * Collection MongoDB: badges_v2
 *
 * Funcionalidades V2:
 * - Estrutura hierárquica (parent/child)
 * - Campo description
 * - Campo hidden (badges secretas)
 * - Campo position (ordenação)
 * - Progresso por usuário
 */

import {
  GET_BADGES_CHILDREN,
  BADGE_CHILD_ERROR,
  CREATE_BADGE_CHILD,
  UPDATE_BADGE_CHILD,
  DELETE_BADGE_CHILD,
  GET_BADGE_CHILD,
  LOADING_BADGES_CHILDREN,
} from "./types";
import axios from "axios";
import { getCookie } from "../../utils/cookies";

const API_BASE = process.env.REACT_APP_GOLANG_API;

// Helper para configurar autenticação
const setAuthHeader = () => {
  const token = getCookie("_session");
  if (token) {
    try {
      axios.defaults.headers.common["Authorization"] = `Bearer ${JSON.parse(token)}`;
    } catch (error) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }
};

// Criar nova badge
export const createBadgeChild = (payload) => async (dispatch) => {
  try {
    setAuthHeader(); // Configurar autenticação

    const requestData = {
      name: payload.name,
      description: payload.description || "",
      parentId: payload.parentId || null,
      icons: {
        default: payload.icons?.default || payload.icons?.icon16x || "",
        locked: payload.icons?.locked || "",
      },
      hidden: payload.hidden || false,
      position: payload.position || 0,
      criteria: payload.criteria || [],
      metadata: payload.metadata || {},
    };

    const data = await axios.post(
      `${API_BASE}/api/v2/admin/badges`,
      requestData,
    );

    dispatch({
      type: CREATE_BADGE_CHILD,
      payload: data.data,
    });
    return setBadgeChildError("");
  } catch (error) {
    return dispatch({
      type: BADGE_CHILD_ERROR,
      payload: error.response ? error.response.data.error : error.message,
    });
  }
};

export const setBadgeChildError = (message) => (dispatch) => {
  return dispatch({
    type: BADGE_CHILD_ERROR,
    payload: message,
  });
};

// Atualizar badge existente
export const updateBadgeChild = (payload) => async (dispatch) => {
  try {
    setAuthHeader(); // Configurar autenticação

    const requestData = {
      name: payload.name,
      description: payload.description || "",
      icons: {
        default: payload.icons?.default || payload.icons?.icon16x || "",
        locked: payload.icons?.locked || "",
      },
      hidden: payload.hidden || false,
      position: payload.position || 0,
      criteria: payload.criteria || [],
      metadata: payload.metadata || {},
    };

    const data = await axios.put(
      `${API_BASE}/api/v2/admin/badges/${payload.id}`,
      requestData,
    );

    return dispatch({
      type: UPDATE_BADGE_CHILD,
      payload: data.data,
    });
  } catch (error) {
    return dispatch({
      type: BADGE_CHILD_ERROR,
      payload: error.response ? error.response.data.error : error.message,
    });
  }
};

// Atualizar parent de uma badge (hierarquia)
export const setBadgeParent = (badgeId, parentId) => async (dispatch) => {
  try {
    setAuthHeader(); // Configurar autenticação

    const data = await axios.patch(
      `${API_BASE}/api/v2/admin/badges/${badgeId}/parent`,
      { parentId },
    );

    return dispatch({
      type: UPDATE_BADGE_CHILD,
      payload: data.data,
    });
  } catch (error) {
    return dispatch({
      type: BADGE_CHILD_ERROR,
      payload: error.response ? error.response.data.error : error.message,
    });
  }
};

// Buscar badge específica
export const getBadgeChild = (badgeChildId) => async (dispatch) => {
  try {
    setAuthHeader(); // Configurar autenticação

    const data = await axios.get(
      `${API_BASE}/api/v2/admin/badges/${badgeChildId}?timestamp=${new Date().getTime()}`,
    );

    return dispatch({
      type: GET_BADGE_CHILD,
      payload:
        data.data && data.data.data
          ? data.data.data
          : data.data
            ? data.data
            : {
                name: "",
                description: "",
                icons: {},
                position: 0,
                hidden: false,
              },
    });
  } catch (error) {
    return dispatch({
      type: BADGE_CHILD_ERROR,
      payload: error.response ? error.response.data.error : error.message,
    });
  }
};

// Buscar árvore hierárquica de badges
export const getBadgesChildren = () => async (dispatch) => {
  // Disparar loading antes de fazer a chamada
  dispatch({
    type: LOADING_BADGES_CHILDREN,
  });

  try {
    // Adicionar showHidden=true para exibir badges hidden no CMS admin
    const data = await axios.get(
      `${API_BASE}/api/v2/badges/tree?showHidden=true`,
    );

    // Backend retorna { data: [...] } ou { badges: [...] }
    const badges = data.data?.data || data.data?.badges || data.data || [];

    return dispatch({
      type: GET_BADGES_CHILDREN,
      payload: badges,
    });
  } catch (error) {
    return dispatch({
      type: BADGE_CHILD_ERROR,
      payload: error.response
        ? error.response.data.error
        : error.message
          ? error.message
          : error.error,
    });
  }
};

// Deletar badge
export const deleteBadgeChild = (badgeId) => async (dispatch) => {
  try {
    setAuthHeader(); // Configurar autenticação

    const data = await axios.delete(
      `${API_BASE}/api/v2/admin/badges/${badgeId}`,
    );

    return dispatch({
      type: DELETE_BADGE_CHILD,
      payload: data.data,
    });
  } catch (error) {
    return dispatch({
      type: BADGE_CHILD_ERROR,
      payload: error.response ? error.response.data.error : error.message,
    });
  }
};
